import { Check, Headphones, Route, Sprout } from 'lucide-react'
import { Plant } from '../components/Plant'
import { PrimaryButton } from '../components/PrimaryButton'
import { concepts, gardenWorlds, graphemeById } from '../data/foundations'
import { currentConcept, lessonLabels, lessonsFor } from '../engine/learningJourney'
import { getAvailableProgressLesson, getNextPractice, type PracticeSession } from '../engine/practiceSession'
import { useProgress } from '../store/ProgressContext'
import type { Lesson } from '../types/content'
import { localDateKey } from '../utils/date'

interface Props { onStart(lesson: Lesson, session?: PracticeSession): void; onOpenMap(): void }

export function HomePage({ onStart, onOpenMap }: Props) {
  const { progress } = useProgress()
  const frontier = currentConcept(progress)
  const next = getNextPractice(progress)
  const nextNewLesson = getAvailableProgressLesson(frontier.id, progress)
  const concept = concepts.find((item) => item.id === next?.lesson.conceptId) ?? frontier
  const list = lessonsFor(concept.id)
  const done = list.filter((lesson) => progress.completedLessons.includes(lesson.id)).length
  const world = gardenWorlds.find((item) => item.stageNumbers.includes(concept.stage)) ?? gardenWorlds[0]
  const display = concept.graphemeIds[0] ? graphemeById.get(concept.graphemeIds[0])?.display : null
  const daily = progress.dailyRecords.find((record) => record.date === localDateKey())?.lessons ?? 0
  const fresh = progress.completedLessons.length === 0
  const practiceLabel = next?.session.mode === 'review' ? next.reason : next?.session.mode === 'transfer' ? '换一个，你来试试' : next?.session.mode === 'independent' ? '这次，自己试一试' : fresh ? '第一小步' : '接着上次的小脚印'
  const buttonLabel = next?.session.mode === 'review' ? '回来练一练' : next?.session.mode === 'transfer' || next?.session.mode === 'independent' ? '我来试一试' : fresh ? '开始听一听' : '继续学习'
  const canContinueNew = next && next.session.mode !== 'lesson' && nextNewLesson && !progress.completedLessons.includes(nextNewLesson.id)
  return (
    <div className="home-layout page-enter">
      <section className="home-garden">
        <div className="flex items-start justify-between gap-3">
          <div><p className="eyebrow">一点声音，一点成长</p><h1 className="mt-3 text-3xl font-black tracking-tight sm:text-[2.65rem]">小花园，今天好。</h1></div>
          <span className="season-label"><Sprout size={17} /> 一起长大</span>
        </div>
        <div className="home-plant"><Plant level={progress.plantLevel} unlockedItems={progress.unlockedItems} /></div>
        <p className="text-center text-sm font-bold text-garden-700">{fresh ? '从一颗小种子开始。' : '你认真听过的声音，都让花园长大了一点。'}</p>
      </section>
      <div className="home-actions">
        <section className="next-step-card">
          <div className="flex items-center justify-between gap-3"><p className="eyebrow text-garden-200">{next ? practiceLabel : '声音正在准备中'}</p><Headphones size={20} className="text-garden-200" /></div>
          <div className="my-7 flex items-center gap-5">
            <div className="target-tile">{display ?? <Headphones size={40} />}</div>
            <div><p className="text-sm font-semibold text-garden-200">{concept.stage === 0 ? '先用小耳朵' : world.title}</p><h2 className="mt-2 text-2xl font-black">{next ? lessonLabels[next.lesson.type] : '先看看花园'}</h2><p className="mt-2 text-sm text-garden-100">{concept.childLabel}</p></div>
          </div>
          {next ? <>
            <PrimaryButton tone="sun" onClick={() => onStart(next.lesson, next.session)}>{buttonLabel}</PrimaryButton>
            {next.session.mode === 'review' && <p className="mt-4 text-center text-sm leading-6 text-garden-100">可以练一小会儿，也可以休息。</p>}
            {canContinueNew && <button onClick={() => onStart(nextNewLesson)} className="mt-3 min-h-12 w-full rounded-xl border border-white/30 px-4 text-sm font-bold text-white">先继续新内容</button>}
          </> : <>
            <p className="mb-5 text-sm leading-6 text-garden-100">这一小步的声音还在准备。可以回到花园，找已经学过的声音练一练。</p>
            <PrimaryButton tone="sun" onClick={onOpenMap}>回到花园路线</PrimaryButton>
          </>}
          <div className="mt-6 flex items-center gap-3" aria-label={`当前小步已完成 ${done} / ${list.length}`}>
            <div className="flex flex-1 gap-1.5">{list.map((lesson) => <span key={lesson.id} className={`h-1.5 flex-1 rounded-full ${progress.completedLessons.includes(lesson.id) ? 'bg-sun' : 'bg-white/20'}`} />)}</div><span className="text-xs font-bold tabular-nums text-garden-100">{done} / {list.length}</span>
          </div>
        </section>
        <section className="daily-card">
          <div><h2 className="font-extrabold">今天的新脚印</h2><p className="mt-1 text-xs text-garden-700">{daily >= 5 ? '今天很棒，随时可以休息。' : '练一点点，休息也很好。'}</p></div>
          <div className="daily-dots" aria-label={`今天新完成 ${daily} 小节`}>{Array.from({ length: 5 }, (_, index) => <span key={index} className={daily > index ? 'filled' : ''}>{daily > index ? <Check size={14} strokeWidth={3} /> : index + 1}</span>)}</div>
        </section>
        <button onClick={onOpenMap} className="route-link"><span className="flex items-center gap-3"><Route size={22} /><span className="text-left"><strong className="block">看看花园路线</strong><span className="text-xs font-medium text-garden-700">{world.title}</span></span></span></button>
      </div>
    </div>
  )
}
